import { GetQueryProps } from "./common";
import z from "zod";
import * as yup from "yup";

//  get all customer sites start
export interface GetAllCustomerSitesInput extends GetQueryProps {
  customerId?: string;
}

const CustomerSiteResultSchema = yup.object().shape({
  mapping_id: yup.string().required(),
  site_name: yup.string().required(),
  email: yup.string().nullable(),
  is_contact_person_as_primary_contact: yup.boolean().nullable(),
  contact_person: yup.string().nullable(),
  contact_number: yup.string().nullable(),
  is_active: yup.boolean().required(),
  country_id: yup.number().nullable(),
  state_id: yup.number().nullable().nullable(),
  country: yup.string().nullable(),
  state: yup.string().nullable(),
  city: yup.string().nullable(),
  zip_code: yup.string().nullable(),
  // The sites endpoint returns a `meta` block alongside each site; `is_default`
  // marks the customer's default site and is what the manual add-user form
  // prefills from. Declared here so consumers get it off `CustomerSiteResultType`
  // instead of re-declaring ad-hoc inline shapes.
  meta: yup
    .object({
      is_default: yup.boolean(),
    })
    .nullable(),
});
export const GetAllCustomerSitesResponseSchema = yup.object().shape({
  totalCount: yup.string().required(),
  totalPages: yup.number(),
  result: yup.array(CustomerSiteResultSchema),
});
export type CustomerSiteResultType = yup.InferType<
  typeof CustomerSiteResultSchema
>;
export type GetAllCustomerSitesResponseType = yup.InferType<
  typeof GetAllCustomerSitesResponseSchema
>;
export interface GetAllUserGroupsInput extends GetQueryProps {
  customerId?: string;
}
//  get all customer sites end

const CustomerGroupResultSchema = z.object({
  mapping_id: z.string(),
  group_name: z.string(),
  group_description: z.string(),
  group_owner_id: z.string(),
  customer_id: z.string(),
  msp_id: z.string(),
  users: z.array(z.any()),
});

export const GetAllUserGroupResponseSchema = z.object({
  totalCount: z.string(),
  totalPages: z.nullable(z.number()),
  result: z.array(CustomerGroupResultSchema),
});

export interface CustomerUserSearchInput {
  searchQuery: string;
  customerId?: string;
  siteId?: string;
}
export interface GetAllCustomerUsersInput extends GetQueryProps {
  customerId?: string;
}
export interface GetAllCustomerUsersByMspIdInput extends GetQueryProps {
  mspId?: string | null;
}

export interface GetCustomerDocumentsInput extends GetQueryProps {
  customerId?: string;
}

export interface GetCustomerUserDocumentsInput extends GetQueryProps {
  customerUserId?: string;
}

export interface GetCustomerVendorsInput extends GetQueryProps {
  customerId?: string;
}
export interface DeleteCustomerDocumentsInput {
  customerId: string;
  attachmentIds: Array<string>;
}

export interface DeleteCustomersInput {
  customerIds: Array<string>;
}
export interface UpdateCustomerNotificationInput {
  notificationType: string;
  notificationStatus: boolean;
  notificationSound: boolean;
  notifyMethods: Array<string>;
  notifyRoles: Array<Record<string, string | null>>;
  customerId: string;
}

export interface UpdateCustomerNotificationsSettingsInput {
  data: any;
  notificationType: string;
  customerId: string;
}

export interface UpdpateCustomerGeneralSettingsInput {
  timezone: any;
  bussiness_hours_from?: string;
  bussiness_hours_tots?: string;
}

export interface SetCustomerUserInput {
  userId: string;
  customerId: string;
  managers: string[];
}

export interface BulkDeleteCustomerUserInput {
  userIds: string[];
}

export interface BulkCreateCustomerUserInput {
  customerId: string;
  file: any;
  send_invite?: boolean;
}
export interface CreateCustomerUserInput {
  customerId: string;
  data: {
    email: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    is_privileged_user?: boolean;
    customer_name: string;
    site: string;
    phone_number?: string;
    country: string;
    state?: string;
    city?: string;
  };
}

export interface CreateCustomerUserOnboarding {
  customerId: string;
  users: BatchUserPayload[];
  send_invite?: boolean;
}

export interface UpdateCustomerUserInput {
  customerUserId: string;
  data: {
    /** Category `mapping_id`, same key the batch-create payload uses. */
    customer_user_type?: string;
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    phone_number?: string;
    is_privileged_user?: boolean;
    designation?: string;
    customer_user_department?: string;
    country_id?: string;
    state_id?: string;
    city?: string;
    address_1?: string;
    address_2?: string;
    street?: string;
    zip_code?: string;
    contact_number?: string;
    is_active?: boolean;
    is_locked?: boolean;
  };
}

export interface CreateCustomerInput {
  customer_name: string;
  domain_name?: string[];
  country: string;
  state?: string;
  city?: string;
  street?: string;
}

export interface CusotomerBillingInvoice {
  template_id: string;
  // invoice_number_settings: string;
  invoice_number_prefix: string;
  // invoice_number: string;
  // invoice_number_starts_from: string;
  invoice_number_starts_from: number;
  email_recipients_cc: {
    email: string;
    user_id?: string | undefined;
    type: string;
  }[];
  email_recipients_to: {
    email: string;
    user_id?: string | undefined;
    type: string;
  }[];
  address_1: string;
  address_2: string;
  street: string;
  country: string;
  state: string;
  postal_code: string;
  city: string;
}

export type NoteDataType = {
  title: string;
  note: string;
};

export type AddNewCustomerNoteInput = {
  customerId: string;
  noteData: NoteDataType;
};

export type AddNewCustomerUserNoteInput = {
  customerUserId: string;
  noteData: NoteDataType;
};

export type UpdateCustomerDefaultSla = {
  customerId: string;
  slaId: string;
};

export type UpdateCustomerContactsInput = {
  customerId: string;
  data: {
    primary_contact_mapping_id: string | null;
    secondary_contact_mapping_id: string | null;
    owner_mapping_id: string | null;
  };
};

export interface UpdateUserGroup {
  groupId: string;
  customerId: string;
  data: any;
}

export interface UpdateCustomerSites {
  siteId: string;
  customerId: string;
  data: any;
}

export interface UpdateCustomerVendor {
  vendorId: string;
  customerId: string;
  data: any;
}

export interface UpdateCustomerNote {
  noteId: string;
  customerId: string;
  noteData: any;
}

export interface UpdateCustomerUserNote {
  noteId: string;
  customerUserId: string;
  noteData: any;
}

export type CustomerUserCategoryTier =
  | "customer_owner"
  | "vip"
  | "manager"
  | "standard";

/**
 * A customer-user category from GET /configuration/user-categories.
 *
 * Live shape confirmed 2026-08-22:
 *   { mapping_id, name, description, level, is_default }
 *
 * Note there is NO `tier` — `level` is the backend's ordering/identity key and
 * is what survives a rename via PATCH /configuration/user-categories/{id}.
 * `tier` is kept optional purely so the local FALLBACK_CATEGORIES (used before
 * the endpoint responds) and the crown-icon mapping still typecheck; it is
 * never present on a real API row.
 */
export interface CustomerUserCategory {
  mapping_id: string;
  name: string;
  is_default: boolean;
  /** Backend ordering key. Lower = more basic; Standard is 10 in the live data. */
  level?: number;
  /** e.g. "Regular user" — shown under the Category field. */
  description?: string;
  tier?: CustomerUserCategoryTier;
}

/** A row queued in the "Ready to invite" pane, not yet sent. */
export interface PendingCustomerUser {
  id: string;
  name: string;
  email: string;
  siteValue: string;
  siteLabel: string | null;
  isPrivileged: boolean;
  categoryValue: string;
  categoryLabel: string | null;
  categoryTier: CustomerUserCategoryTier | null;
  /** Server reason this row failed to send. Cleared when the row is edited. */
  failedReason?: string | null;
}

/** Rename one category. PATCH /configuration/user-categories/{mapping_id}. */
export interface UpdateCustomerUserCategoryInput {
  mappingId: string;
  name: string;
}

export interface AddCustomerUserFormValues {
  name: string;
  email: string;
  site: string;
  permission: "privileged" | "standard";
  category: string;
}

/** One user inside the manual batch-create payload. */
export interface BatchUserPayload {
  full_name: string;
  email: string;
  IsPrivileged: boolean;
  site_id?: string;
  /** The chosen category's `mapping_id`. Backend contract key — not `category`. */
  customer_user_type?: string;
}
